import React from 'react';
import { PreAuthNavbar } from '@/components/navigation/PreAuthNavbar';
import { PostAuthNavbar } from '@/components/navigation/PostAuthNavbar';
import { Footer } from '@/components/marketing/Footer';
import { useAuth } from '@/providers/AuthProvider';

const TermsPage: React.FC = () => {
    const { isAuthenticated } = useAuth();

    return (
        <div className="min-h-screen flex flex-col">
            {isAuthenticated ? <PostAuthNavbar /> : <PreAuthNavbar />}
            <main className="flex-1 max-w-4xl mx-auto px-4 py-16">
                <h1 className="text-4xl font-bold mb-8 text-center">Terms of Service</h1>
                <p className="text-lg mb-8 text-muted-foreground text-center">
                    Welcome to Arrowise. By using our service, you agree to the following terms and conditions. Please read them carefully.
                </p>

                <div className="space-y-8">
                    <section>
                        <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
                        <p className="mb-4">
                            By accessing or using Arrowise, you agree to be bound by these Terms of Service, our Privacy Policy, and all applicable laws and regulations. If you do not agree, please do not use our service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
                        <p className="mb-4">
                            Arrowise is an AI-assisted technical interview platform that enables teams to conduct asynchronous candidate assessments. Our services include:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Interactive coding environments with AI chat assistance</li>
                            <li>Session recording and replay capabilities</li>
                            <li>Bring Your Own API Key (BYOK) functionality for AI providers</li>
                            <li>Team management and billing features</li>
                            <li>AI-assisted candidate assessment tools (Coming soon)</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">3. User Accounts and Responsibilities</h2>
                        <p className="mb-4">
                            You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Provide accurate and complete information when registering</li>
                            <li>Use the service only for lawful purposes</li>
                            <li>Not attempt to circumvent security measures or access unauthorized features</li>
                            <li>Not use the service to create harmful, offensive, or illegal content</li>
                            <li>Respect the rights of other users and third parties</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">4. AI Features and API Keys</h2>
                        <p className="mb-4">
                            Arrowise integrates with AI providers to enhance the interview experience. When using AI features:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>You may provide your own API keys (BYOK) for supported providers</li>
                            <li>API keys are encrypted and stored securely</li>
                            <li>You are responsible for the costs associated with your API usage</li>
                            <li>AI-generated content is not guaranteed to be accurate or appropriate</li>
                            <li>You retain ownership of your prompts and code, but grant us a license to process them</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">5. Intellectual Property</h2>
                        <p className="mb-4">
                            Arrowise and its original content, features, and functionality are owned by Arrowise and are protected by copyright, trademark, and other intellectual property laws.
                        </p>
                        <p className="mb-4">
                            You retain ownership of:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Your code and file uploads</li>
                            <li>Your AI prompts and interactions</li>
                            <li>Any original content you create</li>
                        </ul>
                        <p>
                            By using Arrowise, you grant us a limited license to use, store, and process your content solely for providing our services.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">6. Billing and Subscriptions</h2>
                        <p className="mb-4">
                            Arrowise offers different subscription tiers (Free, Starter, Team) with varying features and limits. By subscribing:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>You agree to pay all applicable fees</li>
                            <li>Payments are processed securely through Stripe</li>
                            <li>Subscriptions auto-renew unless cancelled</li>
                            <li>Refunds are handled according to our refund policy</li>
                            <li>Free tier users must use BYOK for AI features</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">7. Limitation of Liability</h2>
                        <p className="mb-4">
                            Arrowise is provided "as is" without warranties of any kind. We are not liable for:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Any damages arising from your use of the service</li>
                            <li>AI-generated content or responses</li>
                            <li>Loss of data or interrupted service</li>
                            <li>Third-party actions or integrations</li>
                        </ul>
                        <p>
                            Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">8. Termination</h2>
                        <p className="mb-4">
                            Either party may terminate this agreement at any time. Upon termination:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Your access to the service will cease</li>
                            <li>We may delete your data after a reasonable retention period</li>
                            <li>Outstanding payments remain due</li>
                            <li>These terms survive termination where applicable</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">9. Future Features</h2>
                        <p className="mb-4">
                            We may introduce new features, including AI-assisted candidate assessments. By continuing to use Arrowise, you agree to any updates to these terms related to new features.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">10. Changes to Terms</h2>
                        <p className="mb-4">
                            We may update these terms at any time. We will notify you of significant changes via email or through our platform. Continued use of Arrowise constitutes acceptance of the new terms.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">11. Governing Law</h2>
                        <p className="mb-4">
                            These terms are governed by the laws of [Your Jurisdiction], without regard to conflict of law principles.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
                        <p className="mb-4">
                            If you have any questions about these terms, please contact us at:
                        </p>
                        <div className="space-y-2">
                            <p><strong>General inquiries:</strong> <a href="mailto:omar.benhmuda@outlook.com" className="text-primary underline">omar.benhmuda@outlook.com</a></p>
                            <p><strong>Support:</strong> <a href="mailto:omar.benhmuda@outlook.com" className="text-primary underline">omar.benhmuda@outlook.com</a></p>
                        </div>
                    </section>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default TermsPage;
